import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { smoothScrollToElement } from '../utils/smoothScroll';

// CSS for wave animation and haptic feedback
const navbarStyles = `
  @keyframes waveVibrate {
    0%, 100% { transform: translateY(0); }
    25% { transform: translateY(-1px); }
    50% { transform: translateY(1px); }
    75% { transform: translateY(-0.5px); }
  }
  
  @keyframes particleFloat {
    0% { transform: translate(0, 0) scale(1); opacity: 1; }
    100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
  }
  
  .navbar-vibrate {
    animation: waveVibrate 0.3s ease-in-out;
  }
  
  .haptic-feedback {
    animation: waveVibrate 0.1s ease-in-out;
  }
  
  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: #FD802E;
    border-radius: 50%;
    pointer-events: none;
    animation: particleFloat 0.8s ease-out forwards;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = navbarStyles;
  document.head.appendChild(styleSheet);
}

// Particle Effect Component
const ParticleEffect = ({ isActive, x, y }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (isActive) {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: Date.now() + i,
        tx: (Math.random() - 0.5) * 100,
        ty: (Math.random() - 0.5) * 100,
      }));
      setParticles(newParticles);
      setTimeout(() => setParticles([]), 800);
    }
  }, [isActive]);

  return (
    <div className="absolute inset-0 overflow-visible pointer-events-none">
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: '50%',
            top: '50%',
            '--tx': `${p.tx}px`,
            '--ty': `${p.ty}px`,
            boxShadow: '0 0 6px #FD802E',
          }}
        />
      ))}
    </div>
  );
};

// Animated Equalizer Logo
const EqualizerLogo = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [bars] = useState([0.4, 0.7, 1, 0.6, 0.8, 0.5, 0.9]);

  return (
    <motion.a
      href="#hero"
      className="flex items-center gap-3 relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="SoundFlowAI Home"
      whileHover={{ scale: 1.02 }}
    >
      
      
      <img
        src="/logo.svg"
        alt="SoundFlowAI logo"
        className="hidden sm:block w-48 h-auto"
      />
    </motion.a>
  );
};

// Dropdown Menu Component
const DropdownMenu = ({ navItems, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    setSelectedItem(item.name);
    setTimeout(() => {
      setIsOpen(false);
      onSelect?.(item);
    }, 150);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Menu Button */}
      <motion.button
        className="relative px-6 py-2 rounded-full bg-dark-deeper border-2 border-neon-orange flex items-center gap-2 z-20"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          boxShadow: isOpen 
            ? '0 0 25px rgba(253, 128, 46, 0.6)' 
            : '0 0 10px rgba(253, 128, 46, 0.3)',
        }}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-neon-orange font-semibold text-sm">Menu</span>
        <motion.svg
          className="w-4 h-4 text-neon-orange"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-48 bg-dark-deeper/95 backdrop-blur-lg rounded-xl border border-neon-orange/30 shadow-lg overflow-hidden z-50"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            role="menu"
          >
            {/* Glow effect at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-orange to-transparent" />
            
            <div className="py-2">
              {navItems.map((item, i) => {
                const isSelected = selectedItem === item.name;
                
                return (
                  <motion.button
                    key={item.name}
                    className={`w-full text-left block px-4 py-3 text-sm font-medium transition-all ${
                      isSelected 
                        ? 'text-neon-orange bg-neon-orange/10' 
                        : 'text-gray-300 hover:text-neon-orange hover:bg-neon-orange/5'
                    }`}
                    onClick={() => handleSelect(item)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ x: 5 }}
                    role="menuitem"
                  >
                    <div className="flex items-center gap-3">
                      {/* Indicator dot */}
                      <motion.div
                        className={`w-2 h-2 rounded-full ${
                          isSelected ? 'bg-neon-orange' : 'bg-neon-orange/30'
                        }`}
                        whileHover={{ scale: 1.5, backgroundColor: '#FD802E' }}
                      />
                      {item.name}
                      
                      {/* Hover arrow */}
                      <motion.svg
                        className="w-4 h-4 ml-auto text-neon-orange opacity-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        whileHover={{ opacity: 1 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </motion.svg>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            
            {/* Bottom glow */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-orange/30 to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Voice Search Microphone
const VoiceSearchMic = () => {
  const [isListening, setIsListening] = useState(false);
  const [pulseRings, setPulseRings] = useState([]);

  useEffect(() => {
    let interval;
    if (isListening) {
      interval = setInterval(() => {
        setPulseRings(prev => [...prev.slice(-3), Date.now()]);
      }, 600);
    } else {
      setPulseRings([]);
    }
    return () => clearInterval(interval);
  }, [isListening]);

  return (
    <motion.button
      className="relative w-10 h-10 rounded-full bg-dark-deeper border border-neon-orange/30 flex items-center justify-center"
      onClick={() => setIsListening(!isListening)}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      animate={{
        borderColor: isListening ? '#FD802E' : 'rgba(253, 128, 46, 0.3)',
        boxShadow: isListening 
          ? '0 0 20px rgba(253, 128, 46, 0.6)' 
          : '0 0 5px rgba(253, 128, 46, 0.2)',
      }}
      aria-label={isListening ? "Stop voice search" : "Start voice search"}
      aria-pressed={isListening}
    >
      {/* Pulse rings */}
      {pulseRings.map(id => (
        <motion.div
          key={id}
          className="absolute inset-0 rounded-full border-2 border-neon-orange"
          initial={{ scale: 1, opacity: 0.8 }}
          animate={{ scale: 2.5, opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      ))}
      
      {/* Microphone icon */}
      <motion.svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        animate={{ 
          color: isListening ? '#FD802E' : '#9CA3AF',
          scale: isListening ? [1, 1.1, 1] : 1,
        }}
        transition={{ 
          scale: { duration: 0.5, repeat: isListening ? Infinity : 0 }
        }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </motion.svg>

      {/* Listening indicator */}
      {isListening && (
        <motion.div
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-neon-orange whitespace-nowrap"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Listening...
        </motion.div>
      )}
    </motion.button>
  );
};

// Mobile Hamburger Menu Component
const MobileHamburgerMenu = ({ navItems, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigate = (item) => {
    setIsOpen(false);
    onSelect?.(item);
  };

  return (
    <>
      {/* Hamburger Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-10 h-10 flex flex-col items-center justify-center gap-1.5"
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        <motion.span
          className="w-6 h-0.5 bg-neon-orange rounded-full origin-center"
          animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3 }}
        />
        <motion.span
          className="w-6 h-0.5 bg-neon-orange rounded-full"
          animate={isOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        />
        <motion.span
          className="w-6 h-0.5 bg-neon-orange rounded-full origin-center"
          animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3 }}
        />
      </motion.button>

      {/* Sliding Menu Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              className="fixed top-0 left-0 h-screen w-64 bg-dark-deeper border-r border-neon-orange/20 z-50 overflow-y-auto"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {/* Header */}
              <div className="p-6 border-b border-neon-orange/10">
                <div className="flex items-center justify-between">
                  <span className="text-neon-orange font-bold text-lg">Menu</span>
                  <motion.button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-neon-orange transition-colors"
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                </div>
              </div>

              {/* Navigation Items */}
              <div className="p-4 space-y-2">
                {navItems.map((item, i) => (
                  <motion.button
                    key={item.name}
                    onClick={() => handleNavigate(item)}
                    className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:text-neon-orange hover:bg-neon-orange/10 transition-all"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ x: 5 }}
                  >
                    <span className="font-medium">{item.name}</span>
                  </motion.button>
                ))}
              </div>

              {/* Divider */}
              <div className="mx-4 border-t border-neon-orange/10" />

              {/* Additional Links */}
              <div className="p-4 space-y-2 text-sm">
                <Link
                  to="/privacy"
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-2 text-gray-400 hover:text-neon-orange transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-2 text-gray-400 hover:text-neon-orange transition-colors"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/veritonetech-ai"
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-2 text-neon-orange font-medium hover:text-orange-400 transition-colors"
                >
                  VeritoneX Product
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Product CTA Button with Morph Animation
const AuthButton = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link to="/veritonetech-ai">
      <motion.div
        className="relative overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="View Product"
      >
        <motion.button
          className="px-5 py-2 rounded-full border-2 border-neon-orange bg-dark-deeper flex items-center justify-center gap-2 whitespace-nowrap"
          animate={{
            boxShadow: isHovered
              ? '0 0 20px rgba(253, 128, 46, 0.6)'
              : '0 0 10px rgba(253, 128, 46, 0.3)',
          }}
        >
          <span className="text-neon-orange font-semibold text-xs">VeritoneX</span>
        </motion.button>
      </motion.div>
    </Link>
  );
};

// Wavy Navbar Shape SVG
const WavyNavbarShape = ({ isVibrating }) => (
  <svg
    className={`absolute inset-0 w-full h-full pointer-events-none ${isVibrating ? 'navbar-vibrate' : ''}`}
    preserveAspectRatio="none"
    viewBox="0 0 1440 80"
  >
    <defs>
      <filter id="navGlow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
      <clipPath id="wavyClip">
        <path d="M0,0 L1440,0 L1440,60 Q1320,80 1200,65 Q1080,50 960,60 Q840,70 720,65 Q600,60 480,70 Q360,80 240,65 Q120,50 0,60 Z" />
      </clipPath>
    </defs>
    
    {/* Background with clip */}
    <rect 
      x="0" 
      y="0" 
      width="1440" 
      height="80" 
      fill="#233D4C" 
      clipPath="url(#wavyClip)"
      fillOpacity="0.95"
    />
    
    {/* Wavy border glow */}
    <motion.path
      d="M0,60 Q120,50 240,65 Q360,80 480,70 Q600,60 720,65 Q840,70 960,60 Q1080,50 1200,65 Q1320,80 1440,60"
      fill="none"
      stroke="#FD802E"
      strokeWidth="2"
      filter="url(#navGlow)"
      initial={{ opacity: 0.5 }}
      animate={{ 
        opacity: [0.5, 0.8, 0.5],
        strokeWidth: [2, 3, 2],
      }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />
  </svg>
);

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isVibrating, setIsVibrating] = useState(false);
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    // Initial vibration on load
    setIsVibrating(true);
    setTimeout(() => setIsVibrating(false), 300);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);
      
      // Vibrate on scroll direction change
      if (Math.abs(currentScrollY - lastScrollY.current) > 100) {
        setIsVibrating(true);
        setTimeout(() => setIsVibrating(false), 300);
        lastScrollY.current = currentScrollY;
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavItem = (item) => {
    const sectionId = item.href?.startsWith('#') ? item.href.slice(1) : null;
    const isHomeRoute = location.pathname === '/';

    if (item.name === 'Pricing') {
      navigate('/pricing');
      return;
    }

    if (item.name === 'Home' || sectionId === 'hero') {
      if (isHomeRoute) {
        smoothScrollToElement('hero', 100);
      } else {
        if (typeof window !== 'undefined') {
          window.__suppressTopScroll = true;
          setTimeout(() => { window.__suppressTopScroll = false; }, 1500);
        }
        navigate('/', { state: { scrollToSection: 'hero' } });
      }
      return;
    }

    if (sectionId) {
      if (isHomeRoute) {
        smoothScrollToElement(sectionId, 100);
      } else {
        if (typeof window !== 'undefined') {
          window.__suppressTopScroll = true;
          setTimeout(() => { window.__suppressTopScroll = false; }, 1500);
        }
        navigate('/', { state: { scrollToSection: sectionId } });
      }
      return;
    }

    // fallback
    navigate('/');
  };

  const navItems = [
    { name: 'Home', href: '#hero' },
    { name: 'Features', href: '#features' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav 
        className={`fixed top-0 left-0 right-0 z-50 hidden md:block transition-all duration-500 ${
          scrolled ? 'shadow-lg' : ''
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Wavy Background Shape */}
        <div className="absolute inset-0 h-20">
          <WavyNavbarShape isVibrating={isVibrating} />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Equalizer Logo */}
            <EqualizerLogo />

            {/* Center: Dropdown Menu */}
            <DropdownMenu navItems={navItems} onSelect={handleNavItem} />

            {/* Right: Voice Search + Auth */}
            <div className="flex items-center gap-4">
              <VoiceSearchMic />
              <AuthButton />
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Top Bar (minimal) */}
      <motion.div
        className="fixed top-0 left-0 right-0 z-40 md:hidden bg-dark-base/90 backdrop-blur-lg border-b border-neon-orange/10"
        initial={{ y: -60 }}
        animate={{ y: 0 }}
      >
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2">
            <MobileHamburgerMenu navItems={navItems} onSelect={handleNavItem} />
            <EqualizerLogo />
          </div>
          <AuthButton />
        </div>
      </motion.div>


    </>
  );
};

export default Navbar;
